import type { ReactNode } from 'react'
import './figure.css'

/**
 * The one figure a record is read for, stated before anything else about it.
 *
 * A total line is a sum at the foot of what makes it up; this is the opposite
 * shape — the number a reader came for, large enough to be read first, with
 * what it is beneath it and the facts that qualify it beneath that. A
 * campaign's amount raised, a billing account's balance. One per surface, since
 * a second headline is a page ranking its numbers.
 *
 * The figure takes the numeric face, the way every figure in this system does.
 *
 * `compact` is the same figure on one line, sized to sit in a row of controls — the KPI a
 * list is read for, beside the tools that narrow it (`TableHeader`'s `kpi`).
 *
 * `statement` is the figure as a statement leads with it: what it is is read
 * first, as the document's section head, and `asks` spends the accent on a
 * figure that asks something of the reader — a bill, never money on account.
 *
 * `lead` reads what the figure is first, in the interface's own voice, for a
 * figure whose meaning turns on a word — owed or held — so the word is read
 * before the number rather than found under it.
 */
export function Figure({
  amount,
  label,
  note,
  compact,
  statement,
  lead,
  asks,
}: {
  amount: string
  /** What the figure is — "raised", "owed". */
  label: ReactNode
  /** The facts that qualify it, read after it. */
  note?: ReactNode
  compact?: boolean
  statement?: boolean
  lead?: boolean
  asks?: boolean
}) {
  return (
    <div
      className={`figure${compact ? ' figure--compact' : ''}`}
      data-statement={statement || undefined}
      data-lead={lead || undefined}
    >
      <p className="figure__amount" data-asks={asks || undefined}>
        <span>{amount}</span>
      </p>
      <p className="figure__label">{label}</p>
      {note ? <p className="figure__note">{note}</p> : null}
    </div>
  )
}
